import {IAppState, AppActionState, AppAction, IAppAction} from './interfaces/IAppState';
import {IMessage} from '../api/interfaces/Messages/IMessage';
import {Reservation} from '../api/model/reservation/reservation.model';
import {ReservationChangeType} from '../api/model/reservation/change/reservation-change.model';
import {Hotel} from '../api/model/hotel/hotel.model';
import {ReservationStayPreferences} from '../api/model/reservation/stay-preferences/reservation-stay-preferences.model';
import {AccountStayPreferences} from '../api/model/user/stay-preferences/stay-preference.model';
import {User} from '../api/model/user/user.model';
import {EarningsEstimate} from '../api/model/reservation/points-estimate';

const initialState: IAppState = {
  state: AppActionState.INITIAL,
  actionType: AppAction.APP_LOAD,
  error: null,
  resDirect: {} as any,
  appConfig: {} as any,
  acceptTerms: false,
  saveToProfile: false,
  me: {} as any,
  translations: [] as any,
  featureFlags: [] as any,
  messages: [] as IMessage[],
  reservation: null as Reservation,
  hotel: null as Hotel,
  stayPreferences: null as ReservationStayPreferences,
  accountStayPreferences: null as AccountStayPreferences,
  reservationChanges: new Map<ReservationChangeType, any>() as Map<ReservationChangeType, any>,
  availableReservation: null as Reservation,
  member: null as User,
  earningsEstimate: null as EarningsEstimate,
  rateDetails: {} as any
};

function getAcceptTerms(state, action: IAppAction): IAppState {
  return Object.assign({}, state, {
    acceptTerms: action.value
  })
}

function getSaveToProfile(state, action: IAppAction): IAppState {
  return Object.assign({}, state, {
    saveToProfile: action.value
  })
}

function assignResDirect(state, action: IAppAction): IAppState {
  return Object.assign({}, state, {
    resDirect: action.value
  });
}

function appConfigBootstrapSuccess(state, action: IAppAction): IAppState {
  return Object.assign({}, state, {
    appConfig: action.value
  });
}

function assignMe(state, action: IAppAction): IAppState {
  return Object.assign({}, state, {
    me: action.value
  });
}

function assignTranslations(state, action: IAppAction): IAppState {
  return Object.assign( {}, state, {
    translations: action.value
  })
}

function assignFeatureFlags(state, action: IAppAction): IAppState {
  return Object.assign( {}, state, {
    featureFlags: action.featureFlags
  })
}

function getAddMessage(state: IAppState, action: IAppAction ) {
  const newMessages = JSON.parse(JSON.stringify(state.messages));

  if (newMessages.indexOf(action.value) < 0) {
    newMessages.push(action.value);
  }
  return Object.assign( {}, state, {
    messages: newMessages
  });
}

const assignReservationChanges = (state: IAppState, action: IAppAction): IAppState => {
  return Object.assign({}, state, {
    reservationChanges: action.reservationChanges,
    error: action.error,
    state: action.state,
    actionType: action.type
  });
};

const assignAvailableReservation = (state: IAppState, action: IAppAction): IAppState => {
  return Object.assign({}, state, {
    availableReservation: action.availableReservation,
    error: action.error,
    state: action.state,
    actionType: action.type
  });
};

const assignReservation = (state: IAppState, action: IAppAction): IAppState => {
  return Object.assign({}, state, {
    reservation: action.reservation,
    error: action.error,
    state: action.state,
    actionType: action.type
  });
};

const assignHotel = (state: IAppState, action: IAppAction): IAppState => {
  return Object.assign({}, state, {
    hotel: action.hotel,
    error: action.error,
    state: action.state,
    actionType: action.type
  });
};

const assignStayPreferences = (state: IAppState, action: IAppAction): IAppState => {
  return Object.assign({}, state, {
    stayPreferences: action.stayPreferences,
    error: action.error,
    state: action.state,
    actionType: action.type
  });
};

const assignAccountStayPreferences = (state: IAppState, action: IAppAction): IAppState => {
  return Object.assign({}, state, {
    accountStayPreferences: action.stayPreferences,
    error: action.error,
    state: action.state,
    actionType: action.type
  });
};

const assignPointsEstimate = (state: IAppState, action: IAppAction): IAppState => {
  return Object.assign({}, state, {
    earningsEstimate: action.earningsEstimate,
    error: action.error,
    state: action.state,
    actionType: action.type
  });
};

const assignMember = (state: IAppState, action: IAppAction): IAppState => {
  return Object.assign({}, state, {
    member: action.member,
    error: action.error,
    state: action.state,
    actionType: action.type
  });
};

function assignRateDetails(state, action: IAppAction): IAppState {
  return Object.assign({}, state, {
    rateDetails: action.value
  })
}

export function reducer (state = initialState, action: IAppAction) {
  switch (action.type) {
    case AppAction.GET_RESDIRECT:
      return assignResDirect(state, action);
    case AppAction.APPCONFIG_BOOTSTRAP:
      return appConfigBootstrapSuccess(state, action);
    case AppAction.GET_ME:
      return assignMe(state, action);
    case AppAction.ACCEPT_TERMS:
      return getAcceptTerms(state, action);
    case AppAction.SAVE_TO_PROFILE:
      return getSaveToProfile(state, action);
    case AppAction.GET_TRANSLATIONS:
      return assignTranslations(state, action);
    case AppAction.GET_FEATURE_FLAGS:
      return assignFeatureFlags(state, action);
    case AppAction.ADD_MESSAGE:
      return getAddMessage(state, action);
    case AppAction.GET_RESERVATION:
    case AppAction.MODIFY_RESERVATION:
    case AppAction.ASSOCIATE_RESERVATION:
    case AppAction.JOIN_ASSOCIATE_RESERVATION:
    case AppAction.CANCEL_RESERVATION:
    case AppAction.ADD_ADDITIONAL_GUEST_NAMES:
      return assignReservation(state, action);
    case AppAction.GET_HOTEL:
      return assignHotel(state, action);
    case AppAction.GET_STAY_PREFERENCES:
    case AppAction.MODIFY_STAY_PREFERENCES:
      return assignStayPreferences(state, action);
    case AppAction.GET_ACCOUNT_STAY_PREFERENCES:
    case AppAction.MODIFY_ACCOUNT_STAY_PREFERENCES:
      return assignAccountStayPreferences(state, action);
    case AppAction.UPDATE_CHANGES:
    case AppAction.LOAD_CHANGES:
    case AppAction.CLEAR_CHANGES:
    case AppAction.CLEAR_ALL_CHANGES:
      return assignReservationChanges(state, action);
    case AppAction.CHECK_AVAILABILITY:
      return assignAvailableReservation(state, action);
    case AppAction.GET_MEMBER:
    case AppAction.CHANGE_MEMBER_STATUS:
      return assignMember(state, action);
    case AppAction.GET_POINTS_ESTIMATE:
      return assignPointsEstimate(state, action);
    case AppAction.GET_RATE_DETAILS:
      return assignRateDetails(state, action);
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/store/reducer.ts